/* ==========================================================================
   Globe hero — interactive 3D globe with collection markers.
   Loads globe.gl (https://globe.gl) from CDN on demand and mirrors the
   production behavior: near-white needle pins, hover tooltip, grouped
   search over pinned locations + the storefront collections index, side
   panel selection, and clamped zoom buttons. Dragging stops the
   auto-rotation and hides the hint pill.
   ========================================================================== */
(function () {
  'use strict';

  var CDN_GLOBE = 'https://cdn.jsdelivr.net/npm/globe.gl@2.30.0/dist/globe.gl.min.js';

  function loadScriptOnce(src, globalCheck) {
    return new Promise(function (resolve, reject) {
      if (globalCheck && globalCheck()) return resolve();
      var existing = document.querySelector('script[data-loaded-src="' + src + '"]');
      if (existing) {
        if (globalCheck && globalCheck()) return resolve();
        existing.addEventListener('load', function () { resolve(); });
        existing.addEventListener('error', function () { reject(new Error('Failed to load ' + src)); });
        return;
      }
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.dataset.loadedSrc = src;
      s.onload = function () { resolve(); };
      s.onerror = function () { reject(new Error('Failed to load ' + src)); };
      document.head.appendChild(s);
    });
  }

  function normalize(str) {
    return (str || '').toString().trim().toLowerCase();
  }

  function safeJsonParse(text) {
    try {
      return JSON.parse(text);
    } catch (e) {
      return null;
    }
  }

  function initSection(sectionEl) {
    if (!sectionEl || sectionEl.__globeHeroInit) return;
    sectionEl.__globeHeroInit = true;

    var configTag = sectionEl.querySelector('[data-globe-hero-config]');
    if (!configTag) return;
    var config = safeJsonParse(configTag.textContent);
    if (!config) return;

    var canvasEl = sectionEl.querySelector('.globe-hero__canvas');
    var searchInput = sectionEl.querySelector('.globe-hero__search-input');
    var resultsEl = sectionEl.querySelector('.globe-hero__search-results');
    var hintEl = sectionEl.querySelector('[data-globe-hint]');
    var zoomBtns = sectionEl.querySelectorAll('[data-zoom]');
    var panelEmpty = sectionEl.querySelector('.globe-hero__panel-empty');
    var panelContent = sectionEl.querySelector('.globe-hero__panel-content');
    var panelTitle = sectionEl.querySelector('.globe-hero__panel-title');
    var panelMeta = sectionEl.querySelector('.globe-hero__panel-meta');
    var panelBlurb = sectionEl.querySelector('.globe-hero__panel-blurb');
    var panelImageLink = sectionEl.querySelector('.globe-hero__panel-image-link');
    var panelImage = sectionEl.querySelector('.globe-hero__panel-image');
    var panelCta = sectionEl.querySelector('.globe-hero__panel-cta');
    var tooltip = sectionEl.querySelector('.globe-hero__tooltip');
    var tooltipTitle = tooltip && tooltip.querySelector('.globe-hero__tooltip-title');
    var tooltipBlurb = tooltip && tooltip.querySelector('.globe-hero__tooltip-blurb');

    var allLocations = config.locations || [];
    var byId = new Map();
    allLocations.forEach(function (l) { byId.set(l.id, l); });

    var globe = null;
    var controls = null;
    var selectedId = null;
    var collectionsCache = null;

    var mouse = { x: 0, y: 0 };
    window.addEventListener('mousemove', function (e) {
      mouse = { x: e.clientX, y: e.clientY };
      if (tooltip && tooltip.classList.contains('is-visible')) {
        tooltip.style.left = (mouse.x + 15) + 'px';
        tooltip.style.top = (mouse.y + 15) + 'px';
      }
    }, { passive: true });

    function showInPanel(data) {
      if (!panelContent) return;
      if (panelEmpty) panelEmpty.hidden = true;
      panelContent.hidden = false;
      var col = data.collection || {};
      if (panelTitle) panelTitle.textContent = col.title || data.city || '';
      if (panelMeta) {
        panelMeta.textContent = data.lat != null && data.lng != null
          ? Number(data.lat).toFixed(2) + '° / ' + Number(data.lng).toFixed(2) + '°'
          : '';
        panelMeta.hidden = !panelMeta.textContent;
      }
      if (panelBlurb) {
        panelBlurb.textContent = data.blurb || '';
        panelBlurb.hidden = !data.blurb;
      }
      if (panelImage) {
        if (col.image) {
          panelImage.src = col.image;
          panelImage.alt = col.title || '';
          panelImageLink.hidden = false;
        } else {
          panelImageLink.hidden = true;
        }
      }
      if (panelImageLink && col.url) panelImageLink.href = col.url;
      if (panelCta && col.url) panelCta.href = col.url;
    }

    function selectLocation(id, fly) {
      var loc = byId.get(id);
      if (!loc) return;
      selectedId = id;
      showInPanel(loc);
      if (globe) {
        globe.pointsData(allLocations);
        if (fly) {
          if (controls) controls.autoRotate = false;
          globe.pointOfView({ lat: loc.lat, lng: loc.lng, altitude: 1.8 }, 1200);
        }
      }
    }

    /* --- Search: pinned locations + storefront collections ------------- */
    function hideResults() {
      if (resultsEl) {
        resultsEl.hidden = true;
        resultsEl.innerHTML = '';
      }
    }

    function renderGroups(groups) {
      if (!resultsEl) return;
      resultsEl.innerHTML = '';
      var any = false;
      groups.forEach(function (group) {
        if (!group.items.length) return;
        any = true;
        var wrap = document.createElement('div');
        wrap.className = 'globe-hero__search-group';
        var label = document.createElement('span');
        label.className = 'globe-hero__search-group-label';
        label.textContent = group.label;
        wrap.appendChild(label);
        group.items.slice(0, 6).forEach(function (item) {
          var b = document.createElement('button');
          b.type = 'button';
          var t = document.createElement('span');
          t.className = 'globe-hero__search-result-title';
          t.textContent = item.title;
          b.appendChild(t);
          if (item.meta) {
            var m = document.createElement('span');
            m.className = 'globe-hero__search-result-meta';
            m.textContent = item.meta;
            b.appendChild(m);
          }
          b.addEventListener('click', item.onSelect);
          wrap.appendChild(b);
        });
        resultsEl.appendChild(wrap);
      });
      resultsEl.hidden = !any;
    }

    function fetchCollections() {
      if (collectionsCache) return Promise.resolve(collectionsCache);
      return fetch((config.collectionsUrl || '/collections.json') + '?limit=250', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          collectionsCache = (data && data.collections) || [];
          return collectionsCache;
        })
        .catch(function () { return []; });
    }

    if (searchInput) {
      searchInput.addEventListener('input', function (e) {
        var val = normalize(e.target.value);
        if (!val) return hideResults();

        var locationMatches = allLocations
          .filter(function (l) {
            return (
              normalize(l.city).indexOf(val) !== -1 ||
              normalize(l.region).indexOf(val) !== -1 ||
              normalize(l.collection && l.collection.title).indexOf(val) !== -1
            );
          })
          .map(function (l) {
            return {
              title: (l.collection && l.collection.title) || l.city,
              meta: l.region && l.region !== l.city ? l.region : '',
              onSelect: function () {
                hideResults();
                searchInput.value = '';
                selectLocation(l.id, true);
              }
            };
          });

        fetchCollections().then(function (cols) {
          if (normalize(searchInput.value) !== val) return;
          var pinned = {};
          allLocations.forEach(function (l) {
            if (l.collection && l.collection.url) pinned[l.collection.url] = true;
          });
          var collectionMatches = cols
            .filter(function (c) { return normalize(c.title).indexOf(val) !== -1; })
            .filter(function (c) { return !pinned['/collections/' + c.handle]; })
            .map(function (c) {
              return {
                title: c.title,
                meta: c.products_count != null ? c.products_count + ' prints' : '',
                onSelect: function () {
                  window.location.href = '/collections/' + c.handle;
                }
              };
            });
          renderGroups([
            { label: 'Locations', items: locationMatches },
            { label: 'Collections', items: collectionMatches }
          ]);
        });
      });

      document.addEventListener('click', function (e) {
        if (resultsEl && !resultsEl.hidden && !sectionEl.querySelector('.globe-hero__search').contains(e.target)) {
          hideResults();
        }
      });
    }

    /* --- Globe --------------------------------------------------------- */
    function startGlobe() {
      if (!canvasEl || typeof window.Globe !== 'function') return;

      var globe2 = window.Globe()(canvasEl);
      globe = globe2;
      globe2.width(canvasEl.offsetWidth);
      globe2.height(canvasEl.offsetHeight);
      globe2.backgroundColor('rgba(0,0,0,0)');

      var textureUrl = config.textureUrl || '';
      if (textureUrl.indexOf('//') === 0) textureUrl = 'https:' + textureUrl;
      if (textureUrl) globe2.globeImageUrl(textureUrl);

      globe2
        .pointColor(function (d) {
          if (d.id === selectedId) return '#ffffff';
          return Number(d.tier || 1) === 1 ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.35)';
        })
        .pointsData(allLocations)
        .pointLat('lat')
        .pointLng('lng')
        .pointRadius(function (d) {
          if (d.id === selectedId) return 0.7;
          return Number(d.tier || 1) === 1 ? 0.45 : 0.3;
        })
        .pointAltitude(function (d) {
          if (d.id === selectedId) return 0.2;
          return Number(d.tier || 1) === 1 ? 0.08 : 0.04;
        })
        .pointsMerge(false)
        .onPointHover(function (point) {
          if (!tooltip) return;
          if (point) {
            if (tooltipTitle) tooltipTitle.textContent = point.city;
            if (tooltipBlurb) tooltipBlurb.textContent = point.region ? ', ' + point.region : '';
            tooltip.classList.add('is-visible');
            tooltip.style.left = (mouse.x + 15) + 'px';
            tooltip.style.top = (mouse.y + 15) + 'px';
            document.body.style.cursor = 'pointer';
          } else {
            tooltip.classList.remove('is-visible');
            document.body.style.cursor = 'default';
          }
        })
        .onPointClick(function (point) { selectLocation(point.id, true); });

      if (config.boundariesGeojsonUrl) {
        fetch(config.boundariesGeojsonUrl)
          .then(function (r) { return r.json(); })
          .then(function (geo) {
            globe2
              .polygonsData(geo.features || [])
              .polygonCapColor(function () { return 'rgba(0,0,0,0)'; })
              .polygonSideColor(function () { return 'rgba(0,0,0,0)'; })
              .polygonStrokeColor(function () { return 'rgba(255,255,255,0.12)'; });
          })
          .catch(function () {});
      }

      globe2.onGlobeReady(function () {
        controls = globe2.controls();
        if (controls) {
          controls.enableZoom = false;
          controls.enablePan = false;
          controls.autoRotate = true;
          controls.autoRotateSpeed = 0.5;
          controls.addEventListener('start', function () {
            controls.autoRotate = false;
            if (hintEl) hintEl.classList.add('is-hidden');
          });
        }
      });

      globe2.pointOfView({ lat: 20, lng: 0, altitude: Number(config.initialAltitude || 2.2) }, 0);

      var zoomBy = function (direction) {
        var currentPov = globe2.pointOfView();
        var newAlt = currentPov.altitude;
        if (direction === 'in') newAlt *= 0.65;
        else newAlt *= 1.35;
        var rawMin = config.minDistance || 140;
        var rawMax = config.maxDistance || 320;
        var minAlt = Math.max(0.1, (rawMin - 100) / 100);
        var maxAlt = Math.max(1.5, (rawMax - 100) / 100);
        if (newAlt < minAlt) newAlt = minAlt;
        if (newAlt > maxAlt) newAlt = maxAlt;
        globe2.pointOfView({ lat: currentPov.lat, lng: currentPov.lng, altitude: newAlt }, 600);
      };
      zoomBtns.forEach(function (btn) {
        btn.addEventListener('click', function () { zoomBy(btn.dataset.zoom); });
      });

      if ('ResizeObserver' in window) {
        new ResizeObserver(function () {
          globe2.width(canvasEl.offsetWidth).height(canvasEl.offsetHeight);
        }).observe(canvasEl);
      }
    }

    /* Pull in the WebGL payload only when the section nears the viewport. */
    function boot() {
      loadScriptOnce(CDN_GLOBE, function () { return typeof window.Globe === 'function'; })
        .then(startGlobe)
        .catch(function () {
          if (panelEmpty) {
            var msg = panelEmpty.querySelector('p');
            if (msg) msg.textContent = 'Interactive globe unavailable. Use the search to find your collection.';
          }
        });
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries, obs) {
        if (entries.some(function (e) { return e.isIntersecting; })) {
          obs.disconnect();
          boot();
        }
      }, { rootMargin: '300px' });
      io.observe(sectionEl);
    } else {
      boot();
    }
  }

  function initAll() {
    document.querySelectorAll('[data-globe-hero]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
